// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Selection Tag Component

import { SelectionTag as SelectionTagType } from '@/types/selection';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { TAG_CATEGORIES } from '@/utils/selectionUtils';

interface SelectionTagProps {
  tag: SelectionTagType;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onClick?: () => void;
  removable?: boolean;
  onRemove?: () => void;
}

export function SelectionTag({
  tag,
  size = 'md',
  className = '',
  onClick,
  removable = false,
  onRemove
}: SelectionTagProps) {
  // Size mappings
  const sizeMap = {
    sm: 'text-xs py-0.5 px-1.5',
    md: 'text-sm py-1 px-2',
    lg: 'text-base py-1.5 px-3'
  };
  
  // Get category color
  const categoryColor = tag.color || TAG_CATEGORIES[tag.category]?.color || '#94a3b8';
  
  // Create style for the tag
  const tagStyle = {
    backgroundColor: `${categoryColor}20`, // 20% opacity
    color: categoryColor,
    borderColor: categoryColor
  };
  
  return (
    <Badge
      variant="outline"
      className={cn(
        'inline-flex items-center gap-1 rounded-md border font-medium transition-colors cursor-default whitespace-nowrap',
        onClick && 'cursor-pointer hover:bg-opacity-30',
        sizeMap[size],
        className
      )}
      style={tagStyle}
      onClick={onClick}
    >
      <span className="flex-1">{tag.name}</span>
      
      {removable && onRemove && (
        <button
          type="button"
          className="ml-1 rounded-full hover:bg-gray-200 p-0.5 -mr-1"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-3 w-3"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </Badge>
  );
}

interface SelectionTagListProps {
  tags: SelectionTagType[];
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  maxDisplay?: number;
  onTagClick?: (tag: SelectionTagType) => void;
}

export function SelectionTagList({
  tags,
  size = 'md',
  className = '',
  maxDisplay = 0,
  onTagClick
}: SelectionTagListProps) {
  // Sort tags by category
  const sortedTags = [...tags].sort((a, b) => {
    // Sort first by category
    if (a.category !== b.category) {
      const categories = ['growth', 'sensory', 'breeding', 'custom'];
      return categories.indexOf(a.category) - categories.indexOf(b.category);
    }
    // Then by name
    return a.name.localeCompare(b.name);
  });
  
  // Handle max display
  const displayTags = maxDisplay > 0 ? sortedTags.slice(0, maxDisplay) : sortedTags;
  const hasMore = maxDisplay > 0 && sortedTags.length > maxDisplay;
  
  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {displayTags.map(tag => (
        <SelectionTag
          key={tag.id}
          tag={tag}
          size={size}
          onClick={onTagClick ? () => onTagClick(tag) : undefined}
        />
      ))}
      
      {hasMore && (
        <Badge
          variant="outline"
          className={cn(
            'inline-flex items-center rounded-md border border-gray-300 bg-gray-100 text-gray-600 font-medium',
            sizeMap[size]
          )}
        >
          +{sortedTags.length - maxDisplay} more
        </Badge>
      )}
    </div>
  );
}

// Helper type for the size mappings
const sizeMap = {
  sm: 'text-xs py-0.5 px-1.5',
  md: 'text-sm py-1 px-2',
  lg: 'text-base py-1.5 px-3'
}; 